import { useState, useEffect, useCallback, useRef } from 'react';
import { api, fmt } from '../api';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import NavIcon from '../components/NavIcon';
import { confirmDialog } from '../components/ConfirmDialog';

const UNITS = ['pcs', 'kg', 'g', 'litre', 'ml', 'bottle', 'box', 'pack', 'pair', 'dozen'];

function Modal({ prod, cats, onClose, onSave }) {
  const [form, setForm] = useState(prod || { name: '', categoryId: cats[0]?.id || '', salePrice: '', costPrice: '', stock: '', unit: 'pcs' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Only offer active categories when assigning — but if this product's
  // current category has since been made inactive, keep showing it so the
  // existing selection doesn't silently disappear from the dropdown.
  const assignableCats = cats.filter(c => c.isActive || c.id === form.categoryId);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.categoryId) return toast.error('Select a category');
    const payload = { ...form, categoryId: Number(form.categoryId), salePrice: Number(form.salePrice), costPrice: Number(form.costPrice), stock: Number(form.stock) };
    try {
      if (prod?.id) await api.updateProduct(prod.id, payload);
      else await api.createProduct(payload);
      toast.success(prod?.id ? 'Product updated' : 'Product created');
      onSave();
    } catch { toast.error('Failed to save'); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{prod?.id ? 'Edit Product' : 'New Product'}</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="form-row">
          <div className="field"><label>Product Name *</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Coca Cola 500ml" /></div>
          <div className="field"><label>Category *</label>
            <select value={form.categoryId} onChange={e => set('categoryId', e.target.value)}>
              <option value="">Select category</option>
              {assignableCats.map(c => <option key={c.id} value={c.id}>{c.name}{!c.isActive ? ' (Inactive)' : ''}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="field"><label>Sale Price (Rs.)</label><input type="number" min="0" step="0.01" value={form.salePrice} onChange={e => set('salePrice', e.target.value)} placeholder="0.00" /></div>
          <div className="field"><label>Cost Price (Rs.)</label><input type="number" min="0" step="0.01" value={form.costPrice} onChange={e => set('costPrice', e.target.value)} placeholder="0.00" /></div>
        </div>
        <div className="form-row">
          <div className="field"><label>Opening Stock</label><input type="number" min="0" step="0.01" value={form.stock} onChange={e => set('stock', e.target.value)} placeholder="0" /></div>
          <div className="field"><label>Unit</label>
            <select value={form.unit} onChange={e => set('unit', e.target.value)}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save Product</button>
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: 20, totalPages: 1 });
  const [cats, setCats]     = useState([]);
  const [modal, setModal]   = useState(null);

  const [search, setSearch]           = useState('');
  const [categoryId, setCategoryId]   = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(20);

  const searchTimer = useRef();
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const loadProducts = useCallback(() => {
    api.getProducts({ page, pageSize, search: debouncedSearch || undefined, categoryId: categoryId || undefined, stockFilter: stockFilter || undefined })
      .then(setResult).catch(() => toast.error('Failed to load products'));
  }, [page, pageSize, debouncedSearch, categoryId, stockFilter]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { api.getCategories().then(setCats); }, []);

  const toggleActive = async (p) => {
    const next = !p.isActive;
    const verb = next ? 'reactivate' : 'deactivate';
    if (!(await confirmDialog(`Are you sure you want to ${verb} "${p.name}"?${next ? '' : ' It will no longer be available to order, but stays visible in past reports.'}`, { danger: !next, confirmText: verb.charAt(0).toUpperCase() + verb.slice(1) }))) return;
    try {
      await api.setProductActive(p.id, next);
      toast.success(next ? 'Product reactivated' : 'Product deactivated');
      loadProducts();
    } catch { toast.error('Failed to update'); }
  };

  const clearFilters = () => { setSearch(''); setCategoryId(''); setStockFilter(''); setPage(1); };
  const hasFilters = search || categoryId || stockFilter;

  return (
    <div>
      <div className="flex-between mb-6">
        <div className="page-header" style={{margin:0}}>
          <h2>Products</h2>
          <p>{result.totalCount} products in inventory</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>+ New Product</button>
      </div>

      <div className="card">
        <div className="filter-bar">
          <input placeholder="Search products…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select value={categoryId} onChange={e => { setCategoryId(e.target.value); setPage(1); }}>
            <option value="">All categories</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={stockFilter} onChange={e => { setStockFilter(e.target.value); setPage(1); }}>
            <option value="">All stock levels</option>
            <option value="out">Out of stock</option>
            <option value="low">Low stock (≤10)</option>
          </select>
          {hasFilters && <button className="filter-clear" onClick={clearFilters}>✕ Clear</button>}
          <div className="filter-spacer"/>
          <span className="filter-results">{result.totalCount} products</span>
        </div>

        {result.items.length === 0 ? (
          <div className="empty-state"><div className="icon"><NavIcon name="products" size={32} /></div><div>No products found.</div></div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Category</th><th>Sale Price</th><th>Cost Price</th><th>Stock</th><th>Margin</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {result.items.map(p => {
                    const margin = p.salePrice > 0 ? ((p.salePrice - p.costPrice) / p.salePrice * 100).toFixed(1) : 0;
                    return (
                      <tr key={p.id} style={p.isActive ? undefined : { opacity: 0.55 }}>
                        <td style={{fontWeight:600}} data-label="Name">{p.name}</td>
                        <td data-label="Category"><span className="badge badge-blue">{p.category?.name || '—'}</span></td>
                        <td className="mono text-green" data-label="Sale Price">{fmt.currency(p.salePrice)}</td>
                        <td className="mono text-muted" data-label="Cost Price">{fmt.currency(p.costPrice)}</td>
                        <td data-label="Stock">
                          <span className={`badge ${p.stock <= 0 ? 'badge-red' : p.stock <= 10 ? 'badge-warn' : 'badge-green'}`}>
                            {p.stock} {p.unit}
                          </span>
                        </td>
                        <td className="mono" data-label="Margin">{margin}%</td>
                        <td data-label="Status">
                          <span className={`badge ${p.isActive ? 'badge-active' : 'badge-inactive'}`}>
                            {p.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td data-label="">
                          <div className="flex gap-2">
                            <button className="btn btn-secondary btn-sm" onClick={() => setModal(p)}>Edit</button>
                            <button
                              className={`btn btn-sm ${p.isActive ? 'btn-danger' : 'btn-secondary'}`}
                              onClick={() => toggleActive(p)}
                            >
                              {p.isActive ? 'Deactivate' : 'Reactivate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={result.page} totalPages={result.totalPages} pageSize={result.pageSize}
              totalCount={result.totalCount} onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
          </>
        )}
      </div>

      {modal && <Modal prod={modal === 'new' ? null : modal} cats={cats} onClose={() => setModal(null)} onSave={() => { setModal(null); loadProducts(); }} />}
    </div>
  );
}
